import { useState, useEffect } from 'react';
import { Save, RotateCcw, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { useSettings } from '../shared/hooks/useSettings';
import api from 'axios';

export const SettingsPage = () => {
  const { settings, isLoading } = useSettings();
  const [leadTime, setLeadTime] = useState(settings.leadTime);
  const [diasCoberturaAlvo, setDiasCoberturaAlvo] = useState(settings.diasCoberturaAlvo);
  const [savedLeadTime, setSavedLeadTime] = useState(settings.leadTime);
  const [savedDiasCoberturaAlvo, setSavedDiasCoberturaAlvo] = useState(settings.diasCoberturaAlvo);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    setLeadTime(settings.leadTime);
    setDiasCoberturaAlvo(settings.diasCoberturaAlvo);
    setSavedLeadTime(settings.leadTime);
    setSavedDiasCoberturaAlvo(settings.diasCoberturaAlvo);
  }, [settings.leadTime, settings.diasCoberturaAlvo]);

  const handleSave = async () => {
    setIsSaving(true);
    setSaveStatus('idle');
    setErrorMessage('');

    try {
      const response = await api.post<{ success: boolean }>('/sellers/stock-intelligence/settings', {
        lead_time: leadTime,
        cobertura_alvo: diasCoberturaAlvo,
      });

      if (!response.data.success) {
        throw new Error('Erro ao salvar configurações');
      }

      setSavedLeadTime(leadTime);
      setSavedDiasCoberturaAlvo(diasCoberturaAlvo);
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Erro ao salvar configurações no servidor';
      setErrorMessage(message);
      setSaveStatus('error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = async () => {
    setLeadTime(7);
    setDiasCoberturaAlvo(21);
    setIsSaving(true);
    setSaveStatus('idle');
    setErrorMessage('');

    try {
      const response = await api.post<{ success: boolean }>('/sellers/stock-intelligence/settings', {
        lead_time: 7,
        cobertura_alvo: 21,
      });

      if (!response.data.success) {
        throw new Error('Erro ao restaurar configuracoes');
      }

      setSavedLeadTime(7);
      setSavedDiasCoberturaAlvo(21);
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Erro ao restaurar configurações no servidor';
      setErrorMessage(message);
      setSaveStatus('error');
    } finally {
      setIsSaving(false);
    }
  };

  const hasChanges =
    leadTime !== savedLeadTime || diasCoberturaAlvo !== savedDiasCoberturaAlvo;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Configurações</h1>
        <p className="text-gray-600">
          Configure a cobertura alvo e os valores de segurança do sistema de reposição
        </p>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 max-w-2xl">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">Parâmetros de Cálculo</h2>

        <div className="space-y-6">
          <div>
            <label htmlFor="leadTime" className="block text-sm font-medium text-gray-700 mb-2">
              Lead Time (dias)
            </label>
            <p className="text-sm text-gray-500 mb-3">
              Valor de segurança usado somente quando o produto ainda não possui um fornecedor com lead time cadastrado.
            </p>
            <input
              type="number"
              id="leadTime"
              min="1"
              value={leadTime}
              onChange={(e) => setLeadTime(Number(e.target.value))}
              disabled={isSaving}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
          </div>

          <div>
            <label htmlFor="cobertura" className="block text-sm font-medium text-gray-700 mb-2">
              Dias de Cobertura Alvo
            </label>
            <p className="text-sm text-gray-500 mb-3">
              Meta de dias que o estoque deve cobrir após a reposição. Usado para calcular o estoque alvo ideal.
            </p>
            <input
              type="number"
              id="cobertura"
              min="1"
              value={diasCoberturaAlvo}
              onChange={(e) => setDiasCoberturaAlvo(Number(e.target.value))}
              disabled={isSaving}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
          </div>
        </div>

        <div className="mt-8 flex gap-3">
          <button
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
            className={`flex items-center gap-2 px-6 py-2.5 rounded-lg font-medium transition ${
              hasChanges && !isSaving
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {isSaving ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Save className="w-5 h-5" />
            )}
            {isSaving ? 'Salvando...' : 'Salvar Alterações'}
          </button>

          <button
            onClick={handleReset}
            disabled={isSaving}
            className="flex items-center gap-2 px-6 py-2.5 rounded-lg font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RotateCcw className="w-5 h-5" />
            Restaurar Padrões
          </button>
        </div>

        {saveStatus === 'success' && (
          <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-700 flex-shrink-0" />
            <p className="text-sm text-green-900 font-medium">
              Configurações salvas com sucesso! Os cálculos serão atualizados automaticamente.
            </p>
          </div>
        )}

        {saveStatus === 'error' && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-700 flex-shrink-0" />
            <p className="text-sm text-red-900 font-medium">{errorMessage}</p>
          </div>
        )}
      </div>

      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-2xl">
        <h3 className="text-sm font-semibold text-blue-900 mb-2">Como isso afeta os cálculos?</h3>
        <ul className="text-sm text-blue-900 space-y-1 list-disc list-inside">
          <li>O Lead Time global é um fallback; produtos vinculados usam o valor do fornecedor</li>
          <li>A Cobertura Alvo define quanto estoque você deseja manter após a reposição</li>
          <li>Valores mais altos aumentam a segurança mas elevam o custo de estoque</li>
          <li>Todas as mudanças são aplicadas instantaneamente na tabela e no dashboard</li>
        </ul>
      </div>
    </div>
  );
};
